/**
 * TeamPage.tsx — /app/team — members, invites, seats
 *
 * Purpose:    Member list from GET /v1/team, invite-by-email gated by the
 *             tier's seat limit, remove member, owner/member roles.
 * Inputs:     teamApi from @/lib/saas-org; billingApi for tier
 * Outputs:    Team management UI
 * Constraints: Dark-first; WCAG AA. Gateway route rolling out — 404/405/501
 *              renders <ComingOnline/>, never a crash.
 * SOT:        nsentry SaaS completion spec § G-WEB (team/seats)
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { AsyncScreen, type AsyncState } from '@/components/AsyncScreen'
import { ComingOnline } from '@/components/ComingOnline'
import { teamApi, type TeamMember } from '@/lib/saas-org'
import { billingApi } from '@/lib/saas-client'
import { TIERS, type TierId } from '@/lib/tiers'
import { isComingOnline, type SentryError } from '@/lib/sentry-error'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function TeamPage() {
  const [state, setState] = useState<AsyncState>('loading')
  const [members, setMembers] = useState<TeamMember[]>([])
  const [tier, setTier] = useState<TierId>('free')
  const [error, setError] = useState<SentryError | undefined>()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'member' | 'owner'>('member')
  const [formError, setFormError] = useState<string | null>(null)
  const [inviting, setInviting] = useState(false)

  async function load() {
    setState('loading')
    const [teamRes, billingRes] = await Promise.all([teamApi.list(), billingApi.get()])
    if (!teamRes.ok) {
      setError(teamRes.error)
      setState('error')
      return
    }
    setMembers(teamRes.value)
    if (billingRes.ok) setTier(billingRes.value.tier)
    setState('success')
  }

  useEffect(() => {
    void load()
  }, [])

  const seats = TIERS[tier].seats
  const atLimit = members.length >= seats

  async function handleInvite(e: FormEvent) {
    e.preventDefault()
    setFormError(null)
    if (!EMAIL_PATTERN.test(email)) {
      setFormError('Enter a valid email address')
      return
    }
    setInviting(true)
    const result = await teamApi.invite({ email, role })
    setInviting(false)
    if (!result.ok) {
      setFormError(result.error.message)
      return
    }
    setEmail('')
    setRole('member')
    await load()
  }

  async function handleRemove(member: TeamMember) {
    if (!confirm(`Remove ${member.email} from the team? They lose access immediately.`)) return
    const result = await teamApi.remove(member.id)
    if (!result.ok) setFormError(result.error.message)
    await load()
  }

  if (state === 'error' && isComingOnline(error)) {
    return (
      <div className="p-6 lg:p-8">
        <h1 className="mb-6 text-xl font-semibold text-brand-ink">Team</h1>
        <ComingOnline feature="Team management" description="Invite teammates by email and share your monitors, incidents, and status pages." />
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-brand-ink">Team</h1>
        <p className="mt-1 text-sm text-brand-muted">
          {members.length} / {seats} seat{seats === 1 ? '' : 's'} ({TIERS[tier].label})
          {atLimit && (
            <>
              {' '}— <Link to="/app/billing" className="font-medium text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">upgrade for more seats</Link>
            </>
          )}
        </p>
      </div>

      <form onSubmit={handleInvite} className="mb-6 rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Invite teammate">
        {formError && <p className="mb-3 text-sm text-red-700 dark:text-red-300" role="alert">{formError}</p>}
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-56 flex-1">
            <label htmlFor="invite-email" className="mb-1 block text-sm font-medium text-brand-secondary">Invite by email</label>
            <input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@company.com"
              disabled={atLimit}
              className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none disabled:opacity-50"
            />
          </div>
          <div>
            <label htmlFor="invite-role" className="mb-1 block text-sm font-medium text-brand-secondary">Role</label>
            <select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'member' | 'owner')}
              disabled={atLimit}
              className="rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none disabled:opacity-50"
            >
              <option value="member">Member</option>
              <option value="owner">Owner</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={atLimit || inviting}
            className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {inviting ? 'Inviting…' : 'Send invite'}
          </button>
        </div>
        {atLimit && (
          <p className="mt-3 text-xs text-brand-muted">
            Seat limit reached for {TIERS[tier].label}.
          </p>
        )}
      </form>

      <AsyncScreen state={state} error={error} onRetry={load}>
        <div className="overflow-x-auto rounded-xl border border-brand-subtle">
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-surface text-brand-muted">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium">Member</th>
                <th scope="col" className="px-4 py-3 font-medium">Role</th>
                <th scope="col" className="px-4 py-3 font-medium">Status</th>
                <th scope="col" className="px-4 py-3 font-medium">Joined</th>
                <th scope="col" className="px-4 py-3 font-medium"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800 bg-brand-bg">
              {members.map((m) => (
                <tr key={m.id}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-brand-ink">{m.name ?? m.email}</p>
                    {m.name && <p className="text-xs text-brand-muted">{m.email}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${m.role === 'owner' ? 'bg-sky-900/50 text-sky-700 dark:text-sky-300' : 'bg-brand-surface-2 text-brand-secondary'}`}>{m.role}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${m.status === 'active' ? 'bg-green-50 dark:bg-green-900/50 text-green-700 dark:text-green-300' : 'bg-yellow-50 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300'}`}>{m.status}</span>
                  </td>
                  <td className="px-4 py-3 text-brand-muted">{m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {m.role !== 'owner' && (
                      <button type="button" onClick={() => handleRemove(m)} className="text-red-700 dark:text-red-400 hover:text-red-700 dark:text-red-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-400">
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AsyncScreen>
    </div>
  )
}
